'use client';

import Link from 'next/link';
import { getOptimizedImageUrl } from '@/lib/image-utils';

interface FeaturedNewsSectionProps {
  articles?: any[];
}

// Fallback image constants - using getOptimizedImageUrl for consistency
const fallbackImages = [
  getOptimizedImageUrl(null, 800, 600),
  getOptimizedImageUrl("https://images.unsplash.com/photo-1633356122544-f134324a6cee", 800, 600),
  getOptimizedImageUrl("https://images.unsplash.com/photo-1547954575-855750c57bd3", 800, 600),
  getOptimizedImageUrl("https://images.unsplash.com/photo-1515378791036-0648a3ef77b2", 800, 600),
  getOptimizedImageUrl("https://images.unsplash.com/photo-1531297484001-80022131f5a1", 800, 600)
];

export default function FeaturedNewsSection({ articles: propArticles = [] }: FeaturedNewsSectionProps) {
  // Transform WordPress data to match existing structure with fallbacks
  const articles = propArticles && propArticles.length > 0 
    ? propArticles.map((article, index) => {
        const featuredImageSrc = article.featuredImage?.node?.sourceUrl || 
                                article.featuredImage?.node?.mediaItemUrl;
        
        return {
          id: parseInt(article.id) || index + 1,
          slug: article.slug,
          image: getOptimizedImageUrl(featuredImageSrc, 800, 600) || fallbackImages[index] || fallbackImages[0],
          title: article.title || `Article ${index + 1}`,
          category: article.acfNewsFields?.articleCategoryBadge || 'TRENDING',
          date: article.acfNewsFields?.publicationDateOverride || new Date(article.date).toLocaleDateString('en-US', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
          }).toUpperCase().replace(',', ''),
          excerpt: article.excerpt || article.content?.substring(0, 150) + '...' || ''
        };
      })
    : [
        {
          id: 1,
          image: fallbackImages[0],
          title: "Sudowrite 3.0: AI-Driven Plot Twists",
          category: "TRENDING",
          date: "13 MAY, 2025",
          excerpt: "The latest version of Sudowrite introduces revolutionary AI-powered storytelling capabilities."
        },
        {
          id: 2,
          image: fallbackImages[1],
          title: "An Immersive Introduction to Flow TV",
          category: "NEW RELEASE",
          date: "13 MAY, 2025",
          excerpt: "Flow TV launches with an innovative approach to streaming content."
        },
        {
          id: 3,
          image: fallbackImages[2],
          title: "Midjourney v6: Photorealism at Your Fingertips",
          category: "IMAGE GENERATION",
          date: "13 MAY, 2025",
          excerpt: "The newest version of Midjourney achieves unprecedented levels of photorealistic image generation."
        },
        {
          id: 4,
          image: fallbackImages[3],
          title: "Runway Gen-4: Video Magic Unleashed",
          category: "TRENDING",
          date: "13 MAY, 2025",
          excerpt: "Runway's fourth generation video AI model brings Hollywood-quality effects."
        },
        {
          id: 5,
          image: fallbackImages[4],
          title: "Toonify Pro: Cartoon-Style Animation from Photos",
          category: "ANIMATION",
          date: "13 MAY, 2025",
          excerpt: "Transform any photo into professional cartoon-style animations."
        }
      ];

  return (
    <div className="w-full flex justify-center">
      <div className="relative w-full max-w-[1440px] bg-white" id="node-1286_4228">
        <div className="relative size-full">
          <div className="box-border flex flex-col gap-8 sm:gap-10 lg:gap-12 items-start justify-start px-0 py-12 sm:py-16 lg:py-20 relative size-full">
            {/* Header */}
            <div className="relative shrink-0 w-full">
              <div className="flex flex-row items-center relative size-full">
                <div className="box-border flex flex-col sm:flex-row gap-4 sm:gap-0 items-start sm:items-center justify-between px-6 sm:px-8 lg:px-12 py-0 relative w-full">
                  <div className="relative shrink-0 text-[#000000] text-[28px] sm:text-[32px] lg:text-[36px] text-left whitespace-nowrap" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
                    <p className="block leading-[36px] sm:leading-[42px] lg:leading-[48px]">
                      Latest in AI Creativity
                    </p>
                  </div>
                  <Link 
                    href="/news" 
                    className="text-[3.75vw] sm:text-[13px] md:text-[14px] leading-[1] font-ibm-plex-sans-condensed font-medium tracking-[1.2px] sm:tracking-[1.3px] md:tracking-[1.4px] uppercase underline hover:no-underline sm:underline-offset-0 sm:no-underline sm:inline-flex sm:items-center sm:justify-center sm:px-5 sm:md:px-6 sm:py-2.5 sm:h-10 sm:border sm:border-black sm:hover:bg-black sm:hover:text-white transition-all"
                    aria-label="View all AI creative news articles"
                  >
                    ALL NEWS
                  </Link>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="relative shrink-0 w-full">
              <div className="flex flex-row items-center relative size-full">
                <div className="box-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr] gap-6 lg:gap-0 items-start lg:items-stretch justify-start px-6 sm:px-8 md:px-10 lg:px-12 py-0 relative w-full">
                  {/* Main Featured Article - Full width on tablet, left column on desktop */}
                  <Link href={`/news/${(articles[0] as any)?.slug || articles[0]?.id}`} className="w-full md:col-span-2 lg:col-span-1 box-border flex flex-col gap-4 md:gap-5 lg:gap-6 md:h-auto lg:h-[745px] lg:pr-6 items-start justify-start p-0 relative shrink-0 group cursor-pointer hover:opacity-90 transition-opacity duration-200" aria-label={`Read article: ${articles[0]?.title}`} prefetch={true}>
                    {/* Main image container that grows to fill available space */}
                    <div className="h-[280px] sm:h-[320px] md:h-[280px] lg:flex-1 relative shrink-0 w-full overflow-hidden">
                      <div className="absolute inset-0 bg-center bg-cover bg-no-repeat"
                          style={{ backgroundImage: `url('${articles[0]?.image}')` }}>
                        <div className="absolute inset-0 bg-black/20 md:bg-black/10 lg:bg-black/20 lg:group-hover:bg-black/10 transition-all duration-300" />
                      </div>
                    </div>
                    
                    {/* Content - will align with bottom content in adjacent columns */}
                    <div className="box-border flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 text-[#000000] w-full">
                      <div className="box-border flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 text-left w-full">
                        <div className="box-border flex flex-row items-center justify-between p-0 relative shrink-0 uppercase w-full">
                          <div className="flex flex-col font-ibm-plex-sans-condensed justify-center relative shrink-0 text-[12px] sm:text-[13px] lg:text-[14px] whitespace-nowrap tracking-[1.2px] sm:tracking-[1.3px] lg:tracking-[1.4px]">
                            <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                              {articles[0]?.category}
                            </p>
                          </div>
                          <div className="flex flex-col font-ibm-plex-sans-condensed font-medium h-2.5 justify-center relative shrink-0 text-[10px] tracking-[1px] whitespace-nowrap">
                            <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                              {articles[0]?.date}
                            </p>
                          </div>
                        </div>
                        <div className="font-ibm-plex-sans relative shrink-0 text-[14px] sm:text-[16px] lg:text-[18px] w-full">
                          <p className="block leading-[22px] sm:leading-[25px] lg:leading-[28px] line-clamp-2">
                            {articles[0]?.title}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>

                  {/* Middle Column with borders */}
                  <div className="w-full box-border flex flex-col gap-4 md:gap-5 lg:gap-6 lg:border-l lg:border-r lg:border-[#000000] lg:px-6 items-start justify-start p-0 relative shrink-0">
                    {/* Article 2 - Middle Top */}
                    <Link href={`/news/${(articles[1] as any)?.slug || articles[1]?.id}`} className="box-border flex flex-col gap-4 md:gap-5 lg:gap-6 h-auto lg:h-[348px] items-start justify-start p-0 relative shrink-0 w-full group cursor-pointer hover:opacity-90 transition-opacity duration-200" aria-label={`Read article: ${articles[1]?.title}`} prefetch={true}>
                      <div className="h-[180px] sm:h-[220px] md:h-[200px] lg:flex-1 relative shrink-0 w-full overflow-hidden ">
                        <div className="absolute inset-0 bg-center bg-cover bg-no-repeat transition-opacity duration-300"
                            style={{ backgroundImage: `url('${articles[1]?.image}')` }}>
                          <div className="absolute inset-0 bg-black opacity-0 md:opacity-10 lg:opacity-0 lg:group-hover:opacity-10 transition-opacity duration-300" />
                        </div>
                      </div>
                      <div className="box-border flex flex-col gap-6 items-start justify-center p-0 relative shrink-0 text-[#000000] w-full">
                        <div className="box-border flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 text-left w-full">
                          <div className="box-border flex flex-row items-center justify-between p-0 relative shrink-0 uppercase w-full">
                            <div className="flex flex-col font-ibm-plex-sans-condensed justify-center relative shrink-0 text-[12px] sm:text-[13px] lg:text-[14px] whitespace-nowrap tracking-[1.2px] sm:tracking-[1.3px] lg:tracking-[1.4px]">
                              <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                                {articles[1]?.category}
                              </p>
                            </div>
                            <div className="flex flex-col font-ibm-plex-sans-condensed font-medium h-2.5 justify-center relative shrink-0 text-[10px] tracking-[1px] whitespace-nowrap">
                              <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                                {articles[1]?.date}
                              </p>
                            </div>
                          </div>
                          <div className="font-ibm-plex-sans relative shrink-0 text-[14px] sm:text-[16px] lg:text-[18px] w-full">
                            <p className="block leading-[22px] sm:leading-[25px] lg:leading-[28px] line-clamp-2 min-h-[44px] sm:min-h-[50px] lg:min-h-[56px]">
                              {articles[1]?.title}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>

                    {/* Horizontal Divider */}
                    <div className="bg-[#000000] h-px shrink-0 w-full" />

                    {/* Article 4 */}
                    <Link href={`/news/${(articles[3] as any)?.slug || articles[3]?.id}`} className="box-border flex flex-col gap-4 md:gap-5 lg:gap-6 h-auto lg:h-[348px] items-start justify-start p-0 relative shrink-0 w-full group cursor-pointer hover:opacity-90 transition-opacity duration-200" aria-label={`Read article: ${articles[3]?.title}`} prefetch={true}>
                      <div className="h-[180px] sm:h-[220px] md:h-[200px] lg:flex-1 relative shrink-0 w-full overflow-hidden ">
                        <div className="absolute inset-0 bg-center bg-cover bg-no-repeat transition-opacity duration-300"
                            style={{ backgroundImage: `url('${articles[3]?.image}')` }}>
                          <div className="absolute inset-0 bg-black opacity-0 md:opacity-10 lg:opacity-0 lg:group-hover:opacity-10 transition-opacity duration-300" />
                        </div>
                      </div>
                      <div className="box-border flex flex-col gap-6 items-start justify-center p-0 relative shrink-0 text-[#000000] w-full">
                        <div className="box-border flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 text-left w-full">
                          <div className="box-border flex flex-row items-center justify-between p-0 relative shrink-0 uppercase w-full">
                            <div className="flex flex-col font-ibm-plex-sans-condensed justify-center relative shrink-0 text-[12px] sm:text-[13px] lg:text-[14px] whitespace-nowrap tracking-[1.2px] sm:tracking-[1.3px] lg:tracking-[1.4px]">
                              <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                                {articles[3]?.category}
                              </p>
                            </div>
                            <div className="flex flex-col font-ibm-plex-sans-condensed font-medium h-2.5 justify-center relative shrink-0 text-[10px] tracking-[1px] whitespace-nowrap">
                              <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                                {articles[3]?.date}
                              </p>
                            </div>
                          </div>
                          <div className="font-ibm-plex-sans relative shrink-0 text-[14px] sm:text-[16px] lg:text-[18px] w-full">
                            <p className="block leading-[22px] sm:leading-[25px] lg:leading-[28px] line-clamp-2 min-h-[44px] sm:min-h-[50px] lg:min-h-[56px]">
                              {articles[3]?.title}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>

                  {/* Right Column */}
                  <div className="w-full box-border flex flex-col gap-4 md:gap-5 lg:gap-6 lg:pl-6 items-start justify-start p-0 relative shrink-0">
                    {/* Article 3 */}
                    <Link href={`/news/${(articles[2] as any)?.slug || articles[2]?.id}`} className="box-border flex flex-col gap-4 md:gap-5 lg:gap-6 h-auto lg:h-[348px] items-start justify-start p-0 relative shrink-0 w-full group cursor-pointer hover:opacity-90 transition-opacity duration-200" aria-label={`Read article: ${articles[2]?.title}`} prefetch={true}>
                      <div className="h-[180px] sm:h-[220px] md:h-[200px] lg:flex-1 relative shrink-0 w-full overflow-hidden ">
                        <div className="absolute inset-0 bg-center bg-cover bg-no-repeat transition-opacity duration-300"
                            style={{ backgroundImage: `url('${articles[2]?.image}')` }}>
                          <div className="absolute inset-0 bg-black opacity-0 md:opacity-10 lg:opacity-0 lg:group-hover:opacity-10 transition-opacity duration-300" />
                        </div>
                      </div>
                      <div className="box-border flex flex-col gap-6 items-start justify-center p-0 relative shrink-0 text-[#000000] w-full">
                        <div className="box-border flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 text-left w-full">
                          <div className="box-border flex flex-row items-center justify-between p-0 relative shrink-0 uppercase w-full">
                            <div className="flex flex-col font-ibm-plex-sans-condensed justify-center relative shrink-0 text-[12px] sm:text-[13px] lg:text-[14px] whitespace-nowrap tracking-[1.2px] sm:tracking-[1.3px] lg:tracking-[1.4px]">
                              <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                                {articles[2]?.category}
                              </p>
                            </div>
                            <div className="flex flex-col font-ibm-plex-sans-condensed font-medium h-2.5 justify-center relative shrink-0 text-[10px] tracking-[1px] whitespace-nowrap">
                              <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                                {articles[2]?.date}
                              </p>
                            </div>
                          </div>
                          <div className="font-ibm-plex-sans relative shrink-0 text-[14px] sm:text-[16px] lg:text-[18px] w-full">
                            <p className="block leading-[22px] sm:leading-[25px] lg:leading-[28px] line-clamp-2 min-h-[44px] sm:min-h-[50px] lg:min-h-[56px]">
                              {articles[2]?.title}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>

                    {/* Horizontal Divider */}
                    <div className="bg-[#000000] h-px shrink-0 w-full" />

                    {/* Article 5 */}
                    <Link href={`/news/${(articles[4] as any)?.slug || articles[4]?.id}`} className="box-border flex flex-col gap-4 md:gap-5 lg:gap-6 h-auto lg:h-[348px] items-start justify-start p-0 relative shrink-0 w-full group cursor-pointer hover:opacity-90 transition-opacity duration-200" aria-label={`Read article: ${articles[4]?.title}`} prefetch={true}>
                      <div className="h-[180px] sm:h-[220px] md:h-[200px] lg:flex-1 relative shrink-0 w-full overflow-hidden ">
                        <div className="absolute inset-0 bg-center bg-cover bg-no-repeat transition-opacity duration-300"
                            style={{ backgroundImage: `url('${articles[4]?.image}')` }}>
                          <div className="absolute inset-0 bg-black opacity-0 md:opacity-10 lg:opacity-0 lg:group-hover:opacity-10 transition-opacity duration-300" />
                        </div>
                      </div>
                      <div className="box-border flex flex-col gap-6 items-start justify-center p-0 relative shrink-0 text-[#000000] w-full">
                        <div className="box-border flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 text-left w-full">
                          <div className="box-border flex flex-row items-center justify-between p-0 relative shrink-0 uppercase w-full">
                            <div className="flex flex-col font-ibm-plex-sans-condensed justify-center relative shrink-0 text-[12px] sm:text-[13px] lg:text-[14px] whitespace-nowrap tracking-[1.2px] sm:tracking-[1.3px] lg:tracking-[1.4px]">
                              <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                                {articles[4]?.category}
                              </p>
                            </div>
                            <div className="flex flex-col font-ibm-plex-sans-condensed font-medium h-2.5 justify-center relative shrink-0 text-[10px] tracking-[1px] whitespace-nowrap">
                              <p className="block leading-[12px] sm:leading-[13px] lg:leading-[14px]">
                                {articles[4]?.date}
                              </p>
                            </div>
                          </div>
                          <div className="font-ibm-plex-sans relative shrink-0 text-[14px] sm:text-[16px] lg:text-[18px] w-full">
                            <p className="block leading-[22px] sm:leading-[25px] lg:leading-[28px] line-clamp-2 min-h-[44px] sm:min-h-[50px] lg:min-h-[56px]">
                              {articles[4]?.title}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}